import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { EmptyState, PageHead, Pill } from '../../components/ui/common';
import { Icon } from '../../components/ui/icon';
import { useCan } from '../../lib/session';
import { codeTone, copyText, fmtDate, fmtDateTime, fmtNum, fmtTime, relTime } from './format';
import { CreateKeyModal, RevokeKeyModal, RotateKeyModal, SecretModal, UsageModal } from './key-modals';
import { useApiKeys, useApiRequests } from './queries';
import type { ApiKeyView, OneTimeSecret } from './types';
import { InlineError, TableSkel, WebhooksPanel } from './webhooks-panel';

/** Search params of /api-keys: `usage` opens the usage modal of a key, `key` filters "בקשות אחרונות". */
export const apiKeysSearchSchema = z.object({
  usage: z.string().min(1).optional().catch(undefined),
  key: z.string().min(1).optional().catch(undefined),
});
export type ApiKeysSearch = z.infer<typeof apiKeysSearchSchema>;

function curlExample(origin: string) {
  return `curl -X POST ${origin}/api/v1/messages \\
  -H "Authorization: Bearer $NW_API_KEY" \\
  -H "Idempotency-Key: invoice-INV-88-reminder" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": { "clientId": "car2buy", "route": "alerts" },
    "template": "custom_notice",
    "variables": { "title": "עדכון מערכת", "body": "החיבור למרכזייה חזר לפעול" },
    "callbackUrl": "https://billing.nivision.ai/hooks/wa-status"
  }'`;
}

function KeyStatus({ k }: { k: ApiKeyView }) {
  switch (k.status) {
    case 'test':
      return <span className="pill info">בדיקות</span>;
    case 'rotating':
      return (
        <span className="pill warn" title={k.expiresAt ? `יפסיק לעבוד ב-${fmtDateTime(k.expiresAt)}` : undefined}>
          <span className="d" />
          ברוטציה{k.expiresAt ? ` עד ${fmtDate(k.expiresAt)}` : ''}
        </span>
      );
    case 'expired':
      return <Pill tone="">פג תוקף</Pill>;
    case 'revoked':
      return <Pill tone="danger">בוטל</Pill>;
    default:
      return k.isTest ? <span className="pill info">בדיקות</span> : null;
  }
}

export function ApiKeysPage(props: { search: ApiKeysSearch }) {
  const can = useCan();
  const navigate = useNavigate({ from: '/api-keys' });
  const canView = can('api_keys.view');
  const manage = can('api_keys.manage');
  // a role without api_keys.view never calls the endpoints (they answer 403)
  const keys = useApiKeys(canView);
  const requests = useApiRequests(props.search.key, 50, canView);

  const [createOpen, setCreateOpen] = useState(false);
  const [secret, setSecret] = useState<OneTimeSecret | null>(null);
  const [rotating, setRotating] = useState<ApiKeyView | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyView | null>(null);

  const setSearch = (patch: Partial<ApiKeysSearch>) =>
    void navigate({ search: (prev: ApiKeysSearch) => ({ ...prev, ...patch }), replace: true });

  const usageKey = keys.data?.find((k) => k.id === props.search.usage) ?? null;
  const filterKey = keys.data?.find((k) => k.id === props.search.key);

  const head = (
    <PageHead
      title="API ומפתחות"
      lead="מערכות אחרות שולחות הודעות דרך המערכת בבקשת POST. כל הודעה עוברת באותו תור שליחה ובאותן הגנות כמו הודעות מהאתר."
      actions={
        <>
          <a className="btn" href="/api/docs" target="_blank" rel="noopener noreferrer">
            <Icon name="eye" />
            לתיעוד המלא
          </a>
          {manage ? (
            <button className="btn primary" onClick={() => setCreateOpen(true)}>
              <Icon name="plus" />
              מפתח חדש
            </button>
          ) : null}
        </>
      }
    />
  );

  if (!canView) {
    return (
      <>
        <PageHead title="API ומפתחות" />
        <EmptyState icon="shield" title="אין הרשאה" text="למסך הזה צריך הרשאת צפייה במפתחות API. פנו למנהל המערכת." />
      </>
    );
  }

  const curl = curlExample(window.location.origin);

  return (
    <>
      {head}
      <div className="split-wide">
        <div className="stack-lg">
          <div className="panel flush">
            <div className="panel-head">
              <h2>מפתחות</h2>
              <span className="hint">המפתח מוצג פעם אחת בלבד, ביצירה. נשמר אצלנו רק כגיבוב.</span>
            </div>
            {keys.isLoading ? (
              <TableSkel />
            ) : keys.isError ? (
              <InlineError error={keys.error} onRetry={() => void keys.refetch()} />
            ) : !keys.data?.length ? (
              <div className="empty">
                <div className="art">
                  <Icon name="key" />
                </div>
                <h2>אין עדיין מפתחות</h2>
                <p>יוצרים מפתח לכל מערכת ששולחת הודעות (בילינג, פורטל, n8n), עם הרשאות ומכסה משלה.</p>
                {manage ? (
                  <button className="btn primary" onClick={() => setCreateOpen(true)}>
                    <Icon name="plus" />
                    מפתח חדש
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="table-wrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th>שם</th>
                      <th>מפתח</th>
                      <th className="hide-sm">הרשאות</th>
                      <th className="hide-sm">מכסה</th>
                      <th>שימוש אחרון</th>
                      <th>
                        <span className="sr-only">פעולות</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.data.map((k) => {
                      const live = k.status === 'active' || k.status === 'test';
                      return (
                        <tr key={k.id} style={live || k.status === 'rotating' ? undefined : { opacity: 0.6 }}>
                          <td>
                            <div className="cell-title">{k.name}</div>
                            <div className="cell-sub">
                              נוצר {fmtDate(k.createdAt)}
                              {k.createdBy ? ` · ${k.createdBy.name}` : ''}
                            </div>
                          </td>
                          <td>
                            <code className="ltr">{k.prefix}••••</code> <KeyStatus k={k} />
                          </td>
                          <td className="hide-sm">
                            {k.scopes.map((s) => (
                              <span key={s}>
                                <span className="tag ltr">{s}</span>{' '}
                              </span>
                            ))}
                          </td>
                          <td className="hide-sm">
                            <div>{fmtNum(k.dailyQuota)} ביום</div>
                            <div className="cell-sub">
                              {fmtNum(k.requestsToday)} היום · {k.ratePerMin}/דקה
                            </div>
                          </td>
                          <td>{relTime(k.lastUsedAt)}</td>
                          <td className="actions">
                            <button className="btn sm" onClick={() => setSearch({ usage: k.id })}>
                              שימוש
                            </button>
                            {manage && live && !k.replacedById ? (
                              <>
                                {' '}
                                <button className="btn sm" onClick={() => setRotating(k)}>
                                  רוטציה
                                </button>
                              </>
                            ) : null}
                            {manage && (live || k.status === 'rotating') ? (
                              <>
                                {' '}
                                <button className="btn sm danger" onClick={() => setRevoking(k)}>
                                  ביטול
                                </button>
                              </>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="panel flush">
            <div className="panel-head">
              <h2>בקשות אחרונות</h2>
              {keys.data?.length ? (
                <div className="actions">
                  <label className="sr-only" htmlFor="req-key">
                    סינון לפי מפתח
                  </label>
                  <select
                    id="req-key"
                    className="select"
                    style={{ width: 'auto', padding: '4px 10px', fontSize: 13.5 }}
                    value={props.search.key ?? ''}
                    onChange={(e) => setSearch({ key: e.target.value || undefined })}
                  >
                    <option value="">כל המפתחות</option>
                    {keys.data.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.name} ({k.prefix})
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
            {requests.isLoading ? (
              <TableSkel />
            ) : requests.isError ? (
              <InlineError error={requests.error} onRetry={() => void requests.refetch()} />
            ) : !requests.data?.length ? (
              <div className="panel-body small muted">
                {filterKey ? `אין עדיין בקשות עם המפתח ${filterKey.name}.` : 'אין עדיין בקשות. כשמערכת תשלח בקשה עם מפתח, היא תופיע כאן.'}
              </div>
            ) : (
              <div className="table-wrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th>שעה</th>
                      <th>מפתח</th>
                      <th>בקשה</th>
                      <th>תשובה</th>
                      <th className="hide-sm">פירוט</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.data.map((c) => (
                      <tr key={c.id}>
                        <td className="num">{fmtTime(c.at)}</td>
                        <td>{c.keyName}</td>
                        <td>
                          <code className="ltr" style={{ wordBreak: 'break-all' }}>
                            {c.method} {c.path}
                          </code>
                        </td>
                        <td>
                          <span className={`pill ${codeTone(c.statusCode)}`} title={c.errorCode ?? undefined}>
                            {c.statusCode}
                          </span>
                        </td>
                        <td className="hide-sm small muted">{c.note ?? c.errorCode ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <WebhooksPanel />
        </div>

        <div className="stack-lg">
          <div className="panel">
            <div className="panel-head">
              <h2>שליחה בבקשה אחת</h2>
              <span className="hint">שולחים לפי לקוח ומטרה, לא לפי מספר טלפון. המערכת יודעת לאיזו קבוצה זה הולך.</span>
            </div>
            <div className="panel-body">
              <pre
                className="ltr"
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  fontSize: 12.5,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  padding: 12,
                  overflow: 'auto',
                }}
              >
                {curl}
              </pre>
              <div className="row mt-s">
                <button className="btn sm" onClick={() => void copyText(curl)}>
                  <Icon name="copy" />
                  העתקה
                </button>
              </div>
            </div>
          </div>
          <div className="panel">
            <div className="panel-head">
              <h2>מה ה-API יודע לעשות</h2>
            </div>
            <ul className="opt-list small">
              <li>
                <code className="ltr">POST /messages</code>
                <span className="grow muted">שליחה ללקוח, לקבוצה או לאיש קשר. טקסט, תבנית, מדיה, תזמון</span>
              </li>
              <li>
                <code className="ltr">GET /messages/{'{id}'}</code>
                <span className="grow muted">סטטוס: בתור, נשלחה, נמסרה, נקראה, נכשלה</span>
              </li>
              <li>
                <code className="ltr">POST /messages/{'{id}'}/cancel</code>
                <span className="grow muted">ביטול הודעה שעוד בתור</span>
              </li>
              <li>
                <code className="ltr">GET /clients</code>
                <span className="grow muted">לקוחות והמטרות שלהם</span>
              </li>
              <li>
                <code className="ltr">GET /templates</code>
                <span className="grow muted">תבניות והמשתנים שלהן</span>
              </li>
              <li>
                <span className="grow muted">Webhook חתום לכל שינוי סטטוס, לכתובת שבחרתם</span>
              </li>
            </ul>
          </div>
          <div className="callout info">
            <Icon name="shield" />
            <div>
              אותן הגנות כמו באתר: קצב שליחה, שעות שקט, עצירת חירום, ולקוח בטיוטה לא מקבל הודעות. לכל מפתח מכסה משלו, כדי שתקלה במערכת אחת לא
              תשרוף את המספר.
            </div>
          </div>
        </div>
      </div>

      <CreateKeyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(s) => {
          setCreateOpen(false);
          setSecret(s);
        }}
      />
      <RotateKeyModal
        apiKey={rotating}
        onClose={() => setRotating(null)}
        onRotated={(s) => {
          setRotating(null);
          setSecret(s);
        }}
      />
      <RevokeKeyModal apiKey={revoking} onClose={() => setRevoking(null)} />
      <SecretModal secret={secret} onClose={() => setSecret(null)} />
      <UsageModal apiKey={usageKey} onClose={() => setSearch({ usage: undefined })} />
    </>
  );
}
